// DailyDetailDashboard.js - Historical Trading Analysis
// Professional black theme, historical version of TradingCommand
// Shows complete day analysis with AI predictions and execution details

import React, { useEffect, useState, useMemo } from "react";
import { Container, Row, Col, Card, Alert, Button } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { exportPredictions, exportActivities, exportOrders, exportGeneric } from "./components/CSVExport";

// Services - Reuse existing
import { prepareData, predictionAccuracy } from "./service/PredictionAccuracy";
import prepareOrder from "./service/PrepareOrder";
import buildActivities from "./service/BuildActivity";
import config from "./config";
import axios from "axios";
import { getToken } from "./service/AuthService";

// Components - Import from TradingCommand
import MarketChart from "./components/TradingCommand/MarketChart";
import DayPnLSummary from "./components/TradingCommand/DayPnLSummary";
import EvaluationTimeline from "./components/TradingCommand/EvaluationTimeline";
import EvaluationDetailModal from "./components/TradingCommand/EvaluationDetailModal";
import PositionDetailModal from "./components/TradingCommand/PositionDetailModal";
import ClosedPositions from "./components/TradingCommand/ClosedPositions";
import ActivePositions from "./components/TradingCommand/ActivePositions";
import ActivitySummary from "./components/TradingCommand/ActivitySummary";

import { 
  rebuildPositionsFromActivities, 
  calculateTotalPnL 
  } from './service/RebuildPositionsFromActivities';

// Analytics component from old Dashboard
import PredictionChart from "./components/PredictionChart";

// Redux actions
import {
  addOrder,
  addPrediction,
  addActivity,
  clearOrder,
  clearPrediction,
  clearActivity,
} from "./service/actions";

const APIUrl = config.API_BASE_URL;

// API Service - Reuse from Dashboard.js
function getDataAPI(ts, resource) {
  return axios
    .post(
      APIUrl + "/" + resource,
      {
        startDate: ts,
        username: "admin",
        days: 30,
      },
      {
        headers: {
          "x-api-key": config.API_KEY,
          authorizationToken: getToken(),
        },
      }
    )
    .then((response) => {
      console.log(`Loaded ${resource}:`, response.data.length, "items");
      return response;
    })
    .catch((error) => {
      console.error("Error loading data:", error);
      throw error;
    });
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const DailyDetailDashboard = () => {
  // -----------------------------------------------------------------------
  // URL PARAMETER - Get selected date
  // -----------------------------------------------------------------------
  const urlParams = new URLSearchParams(window.location.search);
  const dateParam = urlParams.get("date"); // e.g., "2025-11-03"

  // -----------------------------------------------------------------------
  // RAW STATE (From Backend) - Same structure as TradingCommand
  // -----------------------------------------------------------------------
  const [liveBars, setLiveBars] = useState([]);
  const [cleanbars, setCleanbars] = useState([]);
  const [bars, setBars] = useState([]); // For prediction chart
  const [rawOrders, setRawOrders] = useState([]);
  const [currentPrice, setCurrentPrice] = useState(0);

  // -----------------------------------------------------------------------
  // UI STATE
  // -----------------------------------------------------------------------
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [showEvalModal, setShowEvalModal] = useState(false);
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // -----------------------------------------------------------------------
  // METADATA
  // -----------------------------------------------------------------------
  const [dateInfo, setDateInfo] = useState({
    date: dateParam,
    displayDate: "",
    symbol: "",
  });

  const dispatch = useDispatch();

  // =======================================================================
  // DATA LOADING - Replace socket with backend API calls
  // =======================================================================
  useEffect(() => {
    if (!dateParam) {
      setError("No date specified");
      setLoading(false);
      return;
    }

    const loadHistoricalData = async () => {
      setLoading(true);
      setError(null);

      // Clear existing Redux state
      dispatch(clearPrediction());
      dispatch(clearOrder());
      dispatch(clearActivity());

      try {
        console.log("Ã°Å¸â€œâ€¦ Loading historical data for:", dateParam);

        // Load predictions (bars)
        const predResponse = await getDataAPI(dateParam, "predictions");
        const rawHistory = predResponse.data;

        // Process predictions - PRESERVE EVALUATION FIELDS like TradingCommand
        let outputArr = rawHistory.map((rawBar) => {
          const processedBar = prepareData(rawBar);

          return {
            ...processedBar,
            eval_decision: rawBar.eval_decision,
            eval_type: rawBar.eval_type,
            eval_reasoning: rawBar.eval_reasoning,
            eval_narrative_summary: rawBar.eval_narrative_summary,
            eval_agent_story: rawBar.eval_agent_story,
            eval_position_id: rawBar.eval_position_id,
            eval_new_stop_loss: rawBar.eval_new_stop_loss,
            eval_new_target: rawBar.eval_new_target,
            eval_pattern_influence: rawBar.eval_pattern_influence,
            eval_adjustment_applied: rawBar.eval_adjustment_applied,
            eval_execution_time: rawBar.eval_execution_time,
            eval_error: rawBar.eval_error,
            eval_confidence: rawBar.eval_confidence,
            eval_exit_risk: rawBar.eval_exit_risk,
            position_id: rawBar.position_id || rawBar.eval_position_id,
            decision: rawBar.decision || rawBar.eval_decision || rawBar.signal,
          };
        });
        console.log("Ã¢Å“â€¦ Processed predictions with eval fields:", outputArr.length);

        setCleanbars(outputArr);
        setLiveBars(outputArr);

        // Calculate prediction accuracy
        let cb = predictionAccuracy(config.ACCURACY_WINDOW, 1, outputArr);
        setBars(cb);

        dispatch(addPrediction(outputArr));

        // Set current price and symbol
        if (outputArr.length > 0) {
          const lastBar = outputArr[outputArr.length - 1];
          setCurrentPrice(lastBar.close);
          setDateInfo((prev) => ({
            ...prev,
            symbol: lastBar.symbol || "SPY",
            displayDate: new Date(dateParam).toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            }),
          }));
        }

        // Load orders
        const orderResponse = await getDataAPI(dateParam, "orders");
        const rawData = orderResponse.data;
        console.log("Ã¢Å“â€¦ Loaded orders:", rawData.length);

        setRawOrders(rawData);
        dispatch(addOrder(rawData));

        setLoading(false);
      } catch (err) {
        console.error("Ã¢ÂÅ’ Error loading historical data:", err);
        setError("Failed to load data for this date");
        setLoading(false);
      }
    };

    loadHistoricalData();
  }, [dateParam, dispatch]);

  // =======================================================================
  // CSV EXPORT HANDLERS
  // =======================================================================
  const handleExportPredictions = () => {
    if (liveBars.length > 0) {
      exportPredictions(liveBars, dateParam);
    }
  };

  const handleExportActivities = () => {
    if (activities.length > 0) {
      exportActivities(activities, dateParam);
    }
  };
  const handleExportClosedPositions = () => {
    if (activities.length > 0) {
      exportGeneric(closedPositions, dateParam, 'closed_positions');
    }
  };

  const handleExportActivePositions = () => {
    if (activities.length > 0) {
      exportGeneric(activePositions, dateParam, 'active_positions');
    }
  };
  const handleExportOrders = () => {
    if (rawOrders.length > 0) {
      exportOrders(rawOrders, dateParam);
    }
  };

  // =======================================================================
  // DERIVED STATE - Same as TradingCommand
  // =======================================================================

  // 1. ACTIVITIES - Transform orders into activity records
  const activities = useMemo(() => {
    if (!rawOrders || rawOrders.length === 0) {
      console.log("Ã¢ÂÂ¸Ã¯Â¸Â No orders to process");
      return [];
    }

    try {
      console.log("Ã°Å¸â€â€ž Processing orders into activities:", rawOrders.length);

      const activityArray = [];
      rawOrders.forEach((row) => {
        const [activity, positions] = prepareOrder(row);
        if (activity) {
          activityArray.push(activity);
        }
      });

      if (activityArray.length === 0) {
        console.log("Ã¢Å¡Â Ã¯Â¸Â No valid activities from orders");
        return [];
      }

      const processedActivities = buildActivities(activityArray);
      console.log("Ã¢Å“â€¦ Built activities:", processedActivities.length);

      return processedActivities;
    } catch (err) {
      console.error("Ã¢ÂÅ’ Activity processing error:", err);
      return [];
    }
  }, [rawOrders]);

  // 2. POSITIONS - Build complete position list from activities
  /*
  const positions = useMemo(() => {
    if (!activities || activities.length === 0) {
      console.log("Ã¢ÂÂ¸Ã¯Â¸Â No activities to build positions");
      return [];
    }

    console.log("Ã°Å¸â€œÅ  Building positions from activities");

    // Group activities by trade group
    const tradeGroups = {};
    activities.forEach((activity) => {
      const groupId = activity.group;
      if (!tradeGroups[groupId]) {
        tradeGroups[groupId] = [];
      }
      tradeGroups[groupId].push(activity);
    });

    const allPositions = [];

    Object.keys(tradeGroups).forEach((groupId) => {
      const groupActivities = tradeGroups[groupId].sort(
        (a, b) => new Date(a.created_at_gmt) - new Date(b.created_at_gmt)
      );

      const firstActivity = groupActivities[0];
      const lastActivity = groupActivities[groupActivities.length - 1];
      const openQty = lastActivity.open_qty || 0;

      // Determine position type
      const isLong = firstActivity.side === "buy";
      const entryPrice = parseFloat(firstActivity.price);
      const qty = Math.abs(firstActivity.qty);

      // Find target and stop from orders
      let target = 0;
      let stop = 0;

      rawOrders.forEach((orderRow) => {
        const order = orderRow.order || orderRow;
        if (order.symbol === firstActivity.symbol) {
          const orderTime = new Date(order.created_at);
          const entryTime = new Date(firstActivity.created_at_gmt);

          // Orders created around the same time as entry
          const timeDiff = Math.abs(orderTime - entryTime);
          if (timeDiff < 60000) {
            // Within 1 minute
            const limitPrice = order.limit_price ? parseFloat(order.limit_price) : 0;
            const stopPrice = order.stop_price ? parseFloat(order.stop_price) : 0;

            // Target order
            if (order.type === "limit" && order.side !== firstActivity.side && limitPrice > 0) {
              if ((isLong && limitPrice > entryPrice) || (!isLong && limitPrice < entryPrice)) {
                if (target === 0) target = limitPrice;
              }
            }

            // Stop order
            if ((order.type === "stop" || order.type === "stop_limit") && stopPrice > 0) {
              if ((isLong && stopPrice < entryPrice) || (!isLong && stopPrice > entryPrice)) {
                if (stop === 0) stop = stopPrice;
              }
            }
          }
        }
      });

      // Calculate P&L and exit info
      let exitPrice = 0;
      let exitTime = null;
      let exitType = "Open";
      let unrealizedPL = 0;

      if (openQty === 0) {
        // Position closed
        exitPrice = parseFloat(lastActivity.price);
        exitTime = lastActivity.created_at_gmt;

        // Determine exit type
        if (Math.abs(exitPrice - target) < 0.02 && target > 0) {
          exitType = "Target Fill";
        } else if (Math.abs(exitPrice - stop) < 0.02 && stop > 0) {
          exitType = "Stop Hit";
        } else {
          exitType = "Market Close";
        }

        // Calculate P&L
        if (isLong) {
          unrealizedPL = (exitPrice - entryPrice) * qty;
        } else {
          unrealizedPL = (entryPrice - exitPrice) * qty;
        }
      } else {
        // Position still open
        unrealizedPL = isLong
          ? (currentPrice - entryPrice) * openQty
          : (entryPrice - currentPrice) * openQty;
      }

      // Calculate hold time
      const entryDate = new Date(firstActivity.created_at_gmt);
      const exitDate = exitTime ? new Date(exitTime) : new Date();
      const holdTimeMin = Math.round((exitDate - entryDate) / 60000);

      // Calculate R:R ratio
      let rrRatio = 0;
      if (stop > 0 && target > 0) {
        const risk = Math.abs(entryPrice - stop);
        const reward = Math.abs(target - entryPrice);
        rrRatio = risk > 0 ? (reward / risk).toFixed(2) : 0;
      }

      // Aggregate fees
      const totalTax = groupActivities.reduce((sum, act) => sum + (parseFloat(act.tax) || 0), 0);

      allPositions.push({
        id: groupId,
        a_id: groupId,
        group: groupId,
        symbol: firstActivity.symbol,
        side: firstActivity.side,
        isLong,
        qty,
        open_qty: openQty,
        entryPrice,
        exitPrice,
        exitTime,
        exitType,
        currentPrice: openQty > 0 ? currentPrice : exitPrice,
        unrealizedPL,
        target,
        stop,
        entryTime: firstActivity.created_at_gmt,
        holdTimeMin,
        tax: totalTax,
        isWin: unrealizedPL > 0,
        rrRatio,
        activities: groupActivities,
      });
    });

    console.log("Ã¢Å“â€¦ Built positions:", allPositions.length);
    return allPositions;
  }, [activities, rawOrders, currentPrice]);
  */
  const { positions, activePositions, closedPositions } = useMemo(() => {
    if (!activities || activities.length === 0) {
      console.log("⚠️ No activities to build positions");
      return { 
        positions: [], 
        activePositions: [], 
        closedPositions: [] 
      };
    }

    console.log("🔨 Rebuilding positions from activities (source of truth)");
    
    // Use the rebuild utility
    const rebuilt = rebuildPositionsFromActivities(activities);
    
    console.log("✅ Rebuilt positions:", {
      active: rebuilt.activePositions.length,
      closed: rebuilt.closedPositions.length,
      total: rebuilt.activePositions.length + rebuilt.closedPositions.length
    });
    
    // Combine for backwards compatibility with existing code
    const allPositions = [...rebuilt.activePositions, ...rebuilt.closedPositions];
    
    return {
      positions: allPositions,
      activePositions: rebuilt.activePositions,
      closedPositions: rebuilt.closedPositions
    };
  }, [activities]);

  // 3. METRICS - Calculate summary metrics
  const metrics = useMemo(() => {
    if (!positions || positions.length === 0) {
      return {
        dayPL: 0,
        realizedPL: 0,
        unrealizedPL: 0,
        activePositions: 0,
        closedTrades: 0,
        winRate: 0,
        avgHoldTime: 0,
        medianHoldTime:0,
        bestTrade: 0,
        worstTrade: 0,
        totalFees: 0,
      };
    }

    const closedPos = positions.filter((p) => Number(p?.open_qty ?? 0) === 0);
    const activePos = positions.filter((p) => Number(p?.open_qty ?? 0) > 0);

    //const realizedPL = closedPos.reduce((sum, p) => sum + (parseFloat(p.unrealizedPL) || 0), 0);
    const realizedPL = calculateTotalPnL(closedPositions);
    const unrealizedPL = activePos.reduce((sum, p) => sum + (parseFloat(p.unrealizedPL) || 0), 0);
    const dayPL = realizedPL + unrealizedPL;

    const totalFees = positions.reduce((sum, p) => sum + Math.abs(parseFloat(p.tax) || 0), 0);
    
    const winningTrades = closedPos.filter((p) => p.isWin).length;
    const winRate = closedPos.length > 0 ? ((winningTrades / closedPos.length) * 100).toFixed(0) : 0;

    const avgHoldTime =
      closedPos.length > 0
        ? Math.round(closedPos.reduce((sum, p) => sum + p.holdTimeMin, 0) / closedPos.length)
        : 0;

    
    const pls = closedPos.map((p) => parseFloat(p.unrealizedPL) || 0);
    const bestTrade = pls.length > 0 ? Math.max(...pls) : 0;
    const worstTrade = pls.length > 0 ? Math.min(...pls) : 0;

    const getMedianHoldTime = (positions) => {
      if (positions.length === 0) return 0;
      
      // Extract hold times and filter out any null/undefined values
      const holdTimes = positions
        .map(p => p.holdTimeMin || 0)
        .filter(time => time > 0)
        .sort((a, b) => a - b);
      
      if (holdTimes.length === 0) return 0;
      
      const mid = Math.floor(holdTimes.length / 2);
      
      // If odd number of values, return the middle one
      // If even, return average of two middle values
      if (holdTimes.length % 2 === 0) {
        return Math.round((holdTimes[mid - 1] + holdTimes[mid]) / 2);
      } else {
        return Math.round(holdTimes[mid]);
      }
    };
    
    const medianHoldTime = getMedianHoldTime(closedPositions);

    return {
      dayPL,
      realizedPL,
      unrealizedPL,
      activePositions: activePositions.length,
      closedTrades: closedPositions.length,
      winRate: parseFloat(winRate),
      avgHoldTime,
      medianHoldTime,
      bestTrade,
      worstTrade,
      totalFees,
    };
  }, [positions]);

  /*
  // 4. CLOSED/ACTIVE POSITIONS
  const closedPositions = useMemo(() => {
    return positions.filter((p) => Number(p?.open_qty ?? 0) === 0);
  }, [positions]);

  const activePositions = useMemo(() => {
    return positions.filter((p) => Number(p?.open_qty ?? 0) > 0);
  }, [positions]);
  */

  

  // =======================================================================
  // HANDLERS - Same as TradingCommand (no close action for historical)
  // =======================================================================
  const handlePositionSelect = (position) => {
    setSelectedPosition(position);
    setShowPositionModal(true);
  };

  const handleEvaluationSelect = (evaluation) => {
    setSelectedEvaluation(evaluation);
    setShowEvalModal(true);
  };

  const handlePositionModalClose = () => {
    setShowPositionModal(false);
    setSelectedPosition(null);
  };

  const handleViewFullStory = (position) => {
    setSelectedPosition(position);
    setShowPositionModal(true);
  };

  // Filter chart data for selected position
  const filterChartDataForPosition = (chartData, position) => {
    if (!position || !chartData || chartData.length === 0) return [];

    const entryTime = new Date(position.entryTime).getTime();
    const exitTime = position.exitTime ? new Date(position.exitTime).getTime() : Date.now();

    return chartData.filter((bar) => {
      const barTime = new Date(bar.t || bar.timestamp).getTime();
      return barTime >= entryTime && barTime <= exitTime;
    });
  };

  // Filter evaluations for selected position
  const filterEvaluationsForPosition = (chartData, position) => {
    if (!position || !chartData || chartData.length === 0) return [];

    const entryTime = new Date(position.entryTime).getTime();
    const exitTime = position.exitTime ? new Date(position.exitTime).getTime() : Date.now();

    return chartData.filter((bar) => {
      const barTime = new Date(bar.t || bar.timestamp).getTime();
      return barTime >= entryTime && barTime <= exitTime && bar.evaluation;
    });
  };

  // Prediction chart callback
  const handleDataFromPC = (window, scale) => {
    if (window > 10 && cleanbars.length > 0) {
      let newBars = predictionAccuracy(window, scale, cleanbars);
      setBars(newBars);
    }
  };

  // =======================================================================
  // RENDER
  // =======================================================================

  return (
    <div style={{ backgroundColor: "#000000", minHeight: "100vh" }}>
      <style>{`
        body { background-color: #000000 !important; color: #ffffff !important; }
        .card { background-color: #0a0a0a !important; border: 1px solid #222 !important; color: #ffffff !important; }
        .card-body { background-color: #0a0a0a !important; color: #ffffff !important; }
        .card-header { background-color: #0a0a0a !important; border-bottom: 1px solid #222 !important; color: #ffffff !important; }
        .alert { background-color: #1a0000 !important; border-color: #ff4444 !important; color: #ffcccc !important; }
        .text-muted { color: #888 !important; }
        h1, h2, h3, h4, h5, h6, p, span, div { color: inherit !important; }
      `}</style>

      <Container fluid className="py-3">
        {/* Breadcrumb Navigation */}
        <div style={{ marginBottom: "20px", padding: "10px 0" }}>
          <a
            href="/Trading"
            style={{
              color: "#10b981",
              textDecoration: "none",
              fontSize: "14px",
              transition: "color 0.2s",
            }}
            onMouseEnter={(e) => (e.target.style.color = "#059669")}
            onMouseLeave={(e) => (e.target.style.color = "#10b981")}
          >
            &larr; Back to Trading Dashboard
          </a>
          <span style={{ color: "#555", margin: "0 10px" }}>|</span>
          <span style={{ color: "#e0e0e0", fontSize: "14px" }}>
            {dateInfo.displayDate || dateParam}
          </span>
          {dateInfo.symbol && (
            <span style={{ color: "#888", marginLeft: "10px", fontSize: "14px" }}>
              • {dateInfo.symbol}
            </span>
          )}
        </div>

        {loading && (
          <Card>
            <Card.Body className="text-center py-5">
              <div className="spinner-border text-primary mb-3" role="status"></div>
              <div className="text-muted">Loading historical trading data...</div>
            </Card.Body>
          </Card>
        )}

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            {/* Day P&L Summary - Metrics Banner */}
            <DayPnLSummary
              dayPL={metrics.dayPL}
              realizedPL={metrics.realizedPL}
              unrealizedPL={metrics.unrealizedPL}
              activePositions={metrics.activePositions}
              closedTrades={metrics.closedTrades}
              winRate={metrics.winRate}
              avgHoldTime={metrics.avgHoldTime}
              medianHoldTime={metrics.medianHoldTime}
              bestTrade={metrics.bestTrade}
              worstTrade={metrics.worstTrade}
              totalFees={metrics.totalFees}
            />

            {/* Market Chart - Full Day */}
            <Row className="mt-3">
              <Col xs={12}>
                <Card style={{ backgroundColor: "#0a0a0a", border: "1px solid #222" }}>
                  <Card.Header className="py-2">
                    <h6 className="mb-0" style={{ color: "#ffffff", fontSize: "14px" }}>
                      Market Analysis - Full Day
                    </h6>
                  </Card.Header>
                  <Card.Body className="p-2">
                    {liveBars.length > 0 ? (
                      <MarketChart data={liveBars} selectedPosition={selectedPosition} />
                    ) : (
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: "400px" }}
                      >
                        <div className="text-muted text-center">
                          <div>No chart data available for this date</div>
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Evaluation Timeline */}
            {liveBars.length > 0 && (
              <div className="mt-3">
                <Card style={{ backgroundColor: "#0a0a0a", border: "1px solid #222" }}>
                  <Card.Header className="py-2 d-flex justify-content-between align-items-center">
                    <h6 className="mb-0" style={{ color: "#ffffff", fontSize: "14px" }}>
                      AI Evaluation Timeline
                    </h6>
                    <Button
                      size="sm"
                      variant="outline-light"
                      onClick={handleExportPredictions}
                      style={{ fontSize: "12px", padding: "4px 12px" }}
                    >
                      📥 Export Predictions
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-light"
                      onClick={handleExportActivePositions}
                      style={{ fontSize: "12px", padding: "4px 12px" }}
                    >
                      📥 Export Active Positions
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-light"
                      onClick={handleExportClosedPositions}
                      style={{ fontSize: "12px", padding: "4px 12px" }}
                    >
                      📥 Export Closed Positions
                    </Button>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <EvaluationTimeline
                      predictions={liveBars}
                      onEvaluationSelect={handleEvaluationSelect}
                      selectedEvalId={selectedPosition?.id || selectedPosition?.a_id}
                      positions={positions}
                      onPositionSelect={handlePositionSelect}
                    />
                  </Card.Body>
                </Card>
              </div>
            )}

            {/* Closed Positions Drawer */}
            {closedPositions.length > 0 && (
              <div className="mt-3">
                <ClosedPositions
                  positions={closedPositions}
                  totalRealized={metrics.realizedPL}
                  tradeCount={metrics.closedTrades}
                />
              </div>
            )}

            {/* Activity Summary - Execution Table */}
            {activities.length > 0 && (
              <div className="mt-3">
                <Card style={{ backgroundColor: "#1a1a1a", border: "1px solid #333" }}>
                  <Card.Header
                    style={{
                      backgroundColor: "#2a2a2a",
                      borderBottom: "1px solid #444",
                      padding: "12px 16px",
                    }}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <h6 className="mb-0" style={{ color: "#e0e0e0", fontSize: "14px" }}>
                        📊 Activity Summary
                      </h6>
                      <small style={{ color: "#888", fontSize: "12px" }}>
                        Complete execution history for the day
                      </small>
                    </div>
                    <Button
                      size="sm"
                      variant="outline-light"
                      onClick={handleExportActivities}
                      style={{ fontSize: "12px", padding: "4px 12px" }}
                    >
                      📥 Export Activities
                    </Button>
                  </Card.Header>
                  <Card.Body style={{ padding: "0", backgroundColor: "#0a0a0a" }}>
                    <ActivitySummary data={activities} />
                  </Card.Body>
                </Card>
              </div>
            )}

            {/* Prediction Accuracy Analysis - CRITICAL ANALYTICS */}
            {bars.length > 0 && (
              <div className="mt-3">
                <Card style={{ backgroundColor: "#1a1a1a", border: "1px solid #333" }}>
                  <Card.Header
                    style={{
                      backgroundColor: "#2a2a2a",
                      borderBottom: "1px solid #444",
                      padding: "12px 16px",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-0" style={{ color: "#e0e0e0", fontSize: "14px" }}>
                          Ã°Å¸â€œÅ  Prediction Accuracy Analysis
                        </h6>
                        <small style={{ color: "#888", fontSize: "12px" }}>
                          Analyze model performance by prediction timeframe
                        </small>
                      </div>
                      {rawOrders.length > 0 && (
                        <Button
                          size="sm"
                          variant="outline-light"
                          onClick={handleExportOrders}
                          style={{ fontSize: "12px", padding: "4px 12px" }}
                        >
                          📥 Export Orders
                        </Button>
                      )}
                    </div>
                  </Card.Header>
                  <Card.Body style={{ padding: "16px", backgroundColor: "#0a0a0a" }}>
                    <PredictionChart data={bars} callbackFromParent={handleDataFromPC} />

                    {/* Add Summary Insight */}
                    {bars.length > 0 && (
                      <div
                        style={{
                          marginTop: "15px",
                          padding: "12px",
                          backgroundColor: "#1a1a1a",
                          borderRadius: "4px",
                          border: "1px solid #333",
                        }}
                      >
                        <div style={{ color: "#888", fontSize: "12px", marginBottom: "8px" }}>
                          Ã°Å¸â€™Â¡ Key Insight:
                        </div>
                        <div style={{ color: "#e0e0e0", fontSize: "13px" }}>
                          {(() => {
                            // Calculate average accuracy
                            const accuracies = bars
                              .filter((b) => b.accuracy !== undefined && !isNaN(b.accuracy))
                              .map((b) => b.accuracy);

                            if (accuracies.length === 0) {
                              return "Accuracy data not available for this date.";
                            }

                            const avgAccuracy = (
                              accuracies.reduce((sum, acc) => sum + acc, 0) / accuracies.length
                            ).toFixed(1);

                            if (avgAccuracy >= 60) {
                              return `Ã¢Å“â€¦ Model achieved ${avgAccuracy}% average accuracy today. High confidence - predictions were reliable.`;
                            } else if (avgAccuracy >= 50) {
                              return `Ã¢Å¡Â Ã¯Â¸Â Model achieved ${avgAccuracy}% average accuracy today. Moderate performance - use predictions with caution.`;
                            } else {
                              return `Ã¢ÂÅ’ Model achieved ${avgAccuracy}% average accuracy today. Low performance - model struggled with market conditions.`;
                            }
                          })()}
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </div>
            )}

            {/* No Data Message */}
            {activities.length === 0 && !loading && (
              <Card className="mt-3" style={{ backgroundColor: "#1a1a1a", border: "1px solid #333" }}>
                <Card.Body
                  className="text-center py-5"
                  style={{ backgroundColor: "#0a0a0a", color: "#888" }}
                >
                  <div style={{ fontSize: "48px", marginBottom: "16px", opacity: 0.3 }}>Ã°Å¸â€œÂ­</div>
                  <h5 style={{ color: "#888" }}>No Trading Activity</h5>
                  <p style={{ color: "#666", fontSize: "14px" }}>
                    No trades were executed on this date
                  </p>
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </Container>

      {/* Modals - Same as TradingCommand */}
      <EvaluationDetailModal
        evaluation={selectedEvaluation}
        show={showEvalModal}
        onHide={() => setShowEvalModal(false)}
      />

      <PositionDetailModal
        position={selectedPosition}
        chartData={
          selectedPosition ? filterChartDataForPosition(liveBars, selectedPosition) : []
        }
        evaluations={
          selectedPosition ? filterEvaluationsForPosition(liveBars, selectedPosition) : []
        }
        show={showPositionModal}
        onHide={handlePositionModalClose}
        onViewFullStory={handleViewFullStory}
      />
    </div>
  );
};

export default DailyDetailDashboard;